/**
 * Author: Romain Hapi Sr.
 * Version: 1.1
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Apparition au defilement, discrete : un fondu de 16 px vers le haut, une
 * seule fois. Remplace les `hover:scale-105 rotate-3` qui donnaient au site
 * un aspect agite.
 *
 * `ref` est une *fonction* et non un objet, volontairement. Avec un objet,
 * l'observation etait posee une seule fois, au montage du composant : quand
 * l'element n'apparaissait qu'apres une reponse reseau — les offres d'emploi,
 * la grille de projets filtree — il n'etait jamais observe et restait
 * invisible pour toujours. Une ref-fonction est rappelee par React a chaque
 * attachement ou detachement de l'element, ce qui couvre les deux cas.
 *
 * Respecte `prefers-reduced-motion` : le contenu est alors affiche d'emblee.
 * Si IntersectionObserver manque, on affiche aussi — jamais de page vide.
 */
export const useReveal = () => {
  const [shown, setShown] = useState(false);
  const observerRef = useRef(null);

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const ref = useCallback(
    (node) => {
      disconnect();
      if (!node) return;

      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduced || typeof IntersectionObserver === 'undefined') {
        setShown(true);
        return;
      }

      // Un element deja visible au moment ou il s'attache — c'est le cas d'un
      // contenu charge alors que l'utilisateur regarde deja la section — doit
      // apparaitre tout de suite : l'observateur ne signalera rien de plus.
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShown(true);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [disconnect]
  );

  useEffect(() => disconnect, [disconnect]);

  const className = shown
    ? 'opacity-100 translate-y-0 transition-[opacity,transform] duration-700 ease-out'
    : 'opacity-0 translate-y-4';

  return { ref, shown, className };
};
